import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Card,
  CardBody,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  VStack,
  Text,
  Alert,
  AlertIcon,

} from '@chakra-ui/react'
import { Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { loginSchema, type LoginFormData } from '@/lib/validations/auth'
import { useAuthStore } from '@/store/auth'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'

export function LoginForm() {
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(true)
  const [error, setError] = useState('')

  const { login, isLoading } = useAuthStore()
  const { settings: siteSettings } = useSiteSettings()
  const colors = useThemeColors()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  // Prevent body scroll on login page
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    html.style.overflow = 'hidden'
    html.style.height = '100%'
    body.style.overflow = 'hidden'
    body.style.height = '100%'
    body.style.margin = '0'
    body.style.padding = '0'
    return () => {
      html.style.overflow = ''
      html.style.height = ''
      body.style.overflow = ''
      body.style.height = ''
      body.style.margin = ''
      body.style.padding = ''
    }
  }, [])

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError('')

      const result = await login({ 
        email: data.email, 
        password: data.password, 
        remember_me: rememberMe,
        application: 'admin-support' // Specify admin-support application to get correct routes
      })

      if (result.twoFactorSetupRequired) {
        // Redirect to 2FA setup page
        navigate('/two-factor/setup', { 
          replace: true, 
          state: { 
            email: data.email,
            password: data.password,
            rememberMe 
          } 
        })
        return
      }

      if (result.twoFactorRequired) {
        // Redirect to 2FA verification page
        navigate('/two-factor', { 
          replace: true, 
          state: { 
            email: data.email,
            rememberMe 
          } 
        })
        return
      }

      if (result.success) {
        // Login successful - navigate to dashboard
        // The route will be determined by user_routes from the backend
        navigate('/dashboard', { replace: true })
      } else {
        setError(result.message || 'Login failed')
      }
    } catch (err: any) {
      // Handle error response
      const errorMessage = err.response?.data?.message || err.message || 'Login failed. Please check your credentials.'
      setError(errorMessage)
      
      if (import.meta.env.DEV) {
        console.error('[LoginForm] Login error:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        })
      }
    }
  }

  return (
    <Box 
      h="100vh"
      maxH="100vh"
      bg={colors.bg} 
      display="flex" 
      flexDir="column"
      overflow="hidden"
      w="100%"
    >
      <Box
        flex="1"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={{ base: 4, sm: 6, md: 8 }}
        py={{ base: 4, md: 6 }}
        minH={0}
        overflow="hidden"
        w="100%"
      >
        <Card
          w="full"
          maxW={{ base: '100%', sm: '400px', md: '448px' }}
          bg={colors.cardBg}
          borderRadius={{ base: 'lg', md: 'xl' }}
          boxShadow={{ base: 'sm', md: '0 8px 10px rgba(0,0,0,0.10)' }}
          maxH={{ base: 'calc(100vh - 100px)', md: 'calc(100vh - 120px)' }}
          overflow="hidden"
        >
          <CardBody p={{ base: 4, sm: 5, md: 6 }}>
            <VStack spacing={{ base: 3, md: 4 }} align="stretch">
              <Box display="flex" justifyContent="center" pt={{ base: 0, md: 1 }}>
                <Box
                  as="img"
                  src={siteSettings.auth_logo || '/assets/img/als-logo-2.png'}
                  alt={siteSettings.site_name || 'Actionlabs'}
                  maxW={{ base: '240px', sm: '280px', md: '320px' }}
                  w="100%"
                  objectFit="contain"
                />
              </Box>

              <Box textAlign="center">
                <Heading
                  size={{ base: 'sm', md: 'md' }}
                  color={colors.heading}
                  fontWeight="800"
                  mb={{ base: 0.5, md: 1 }}
                >
                  Log In
                </Heading>
                <Text 
                  fontSize={{ base: 'xs', sm: 'sm' }} 
                  color={colors.textMuted}
                  display={{ base: 'block', sm: 'block' }}
                >
                  Please enter your credentials below to access the{' '}
                  <Box as="span" display={{ base: 'block', sm: 'inline' }}>
                    Helpdesk Portal.
                  </Box>
                </Text>
              </Box>

              {error && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {error}
                </Alert>
              )}

              <Box as="form" onSubmit={handleSubmit(onSubmit)}>
                <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                  <FormControl isInvalid={!!errors.email}>
                    <FormLabel fontSize={{ base: 'xs', sm: 'sm' }} color={colors.heading}>
                      Email
                    </FormLabel>
                    <InputGroup>
                      <InputLeftElement
                        pointerEvents="none"
                        color={colors.textMuted}
                        height="100%"
                      >
                        <Mail size={18} />
                      </InputLeftElement>
                      <Input
                        {...register('email')}
                        type="email"
                        placeholder="Enter your email"
                        pl="10"
                        fontSize={{ base: 'sm', md: 'md' }}
                        bg={colors.inputBg}
                        borderColor={colors.inputBorder}
                        focusBorderColor={colors.email}
                        autoComplete="username"
                      />
                    </InputGroup>
                    <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.password}>
                    <FormLabel fontSize={{ base: 'xs', sm: 'sm' }} color={colors.heading}>
                      Password
                    </FormLabel>
                    <InputGroup>
                      <InputLeftElement
                        pointerEvents="none"
                        color={colors.textMuted}
                        height="100%"
                      >
                        <Lock size={18} />
                      </InputLeftElement>

                      <Input
                        {...register('password')}
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        pl="10"
                        pr="3rem"
                        fontSize={{ base: 'sm', md: 'md' }}
                        bg={colors.inputBg}
                        borderColor={colors.inputBorder}
                        focusBorderColor={colors.email}
                        autoComplete="current-password"
                      />

                      <InputRightElement width="3rem" height="100%">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPassword((s) => !s)}
                          type="button"
                          aria-label={
                            showPassword ? 'Hide password' : 'Show password'
                          }
                          h="1.75rem"
                          minW="1.75rem"
                          color={colors.textSecondary}
                        >
                          {showPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage>
                      {errors.password?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <Box
                    display="flex"
                    flexDirection={{ base: 'column', sm: 'row' }}
                    alignItems={{ base: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                    gap={{ base: 2, sm: 0 }}
                    pt={1}
                  >
                    <Checkbox
                      isChecked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      colorScheme="blue"
                      size={{ base: 'sm', md: 'md' }}
                    >
                      <Text fontSize={{ base: 'xs', sm: 'sm' }} color={colors.text}>
                        Remember Me
                      </Text>
                    </Checkbox>

                    <Button
                      variant="link"
                      color={colors.link}
                      fontSize={{ base: 'xs', sm: 'sm' }}
                      onClick={() => navigate('/forgot-password')}
                      alignSelf={{ base: 'flex-start', sm: 'auto' }}
                      _hover={{
                        color: colors.linkHover,
                        textDecoration: 'underline',
                      }}
                    >
                      Forgot password?
                    </Button>
                  </Box>

                  <Button
                    type="submit"
                    w="full"
                    h={{ base: '42px', md: '44px' }}
                    borderRadius="md"
                    fontSize={{ base: 'sm', md: 'md' }}
                    color="white"
                    bg={colors.primary}
                    _hover={{ bg: colors.primaryHover }}
                    _active={{ filter: 'brightness(1)' }}
                    isLoading={isLoading}
                    loadingText="Signing in..."
                  >
                    Continue
                  </Button>
                </VStack>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      </Box>

      <Box py={{ base: 3, md: 4 }} textAlign="center" flexShrink={0} px={4}>
        <Text fontSize={{ base: 'xs', sm: 'sm' }} color={colors.textSecondary}>
          {new Date().getFullYear()} ©{' '}
          <Box as="span" color={colors.email} fontWeight="600">
            Actionlabs
          </Box>
          , All Rights Reserved
        </Text>
      </Box>
    </Box>
  )
}
